import { supabase } from "@/lib/supabaseClient";
import type { Exam } from "@/types/models";

/** exam_questions + words 조인 결과 */
export type ExamQuestionWithWord = {
  id: string;
  exam_id: string;
  seq: number;
  word_id: string | null;
  option_1: string;
  option_2: string;
  option_3: string;
  option_4: string;
  correct_option: number;
  selected_option: number | null;
  is_correct: boolean | null;
  answered_at: string | null;
  words: { word: string; meaning: string } | null;
};

/** 특정 시험 단건 조회 */
export async function getExam(examId: string): Promise<Exam> {
  const { data, error } = await supabase.from("exams").select("*").eq("id", examId).single();

  if (error) {
    throw error;
  }
  return data;
}

/** 사용자의 시험 목록 조회 (날짜 내림차순) */
export async function getExams(userId: string): Promise<Exam[]> {
  const { data, error } = await supabase
    .from("exams")
    .select("*")
    .eq("user_id", userId)
    .order("exam_date", { ascending: false });

  if (error) {
    throw error;
  }
  return data;
}

/** 특정 날짜의 일일 테스트 조회 */
export async function getDailyExam(userId: string, examDate: string): Promise<Exam | null> {
  const { data, error } = await supabase
    .from("exams")
    .select("*")
    .eq("user_id", userId)
    .eq("exam_date", examDate)
    .eq("exam_type", "daily")
    .maybeSingle();

  if (error) {
    throw error;
  }
  return data;
}

/** 특정 시험의 재시험 목록 조회 */
export async function getRetryExams(parentExamId: string): Promise<Exam[]> {
  const { data, error } = await supabase
    .from("exams")
    .select("*")
    .eq("parent_exam_id", parentExamId)
    .order("retry_round");

  if (error) {
    throw error;
  }
  return data;
}

/** 시험 문항 조회 (seq 순, words 조인) */
export async function getExamQuestions(examId: string): Promise<ExamQuestionWithWord[]> {
  const { data, error } = await supabase
    .from("exam_questions")
    .select(
      "id, exam_id, seq, word_id, option_1, option_2, option_3, option_4, correct_option, selected_option, is_correct, answered_at, words(word, meaning)",
    )
    .eq("exam_id", examId)
    .order("seq");

  if (error) {
    throw error;
  }

  return data as ExamQuestionWithWord[];
}

/** Playwright 등 E2E에서 Vite에 `VITE_E2E_EXAM_QUESTION_COUNT`를 주입하면 요청 값 대신 짧은 시험지로 생성 */
function resolveExamQuestionCount(requested: number): number {
  const raw = import.meta.env.VITE_E2E_EXAM_QUESTION_COUNT;
  if (typeof raw !== "string" || raw.trim() === "") {
    return requested;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return requested;
  }

  return Math.min(Math.floor(parsed), 500);
}

/** 일일 테스트 생성 (PL/pgSQL 함수 호출) */
export async function generateExam(examDate: string, questionCount = 500): Promise<string> {
  const { data, error } = await supabase.rpc("generate_exam", {
    p_exam_date: examDate,
    p_question_count: resolveExamQuestionCount(questionCount),
  });

  if (error) {
    throw error;
  }
  return data as string;
}

/** 오답 재시험 생성 (PL/pgSQL 함수 호출) */
export async function generateRetryExam(parentExamId: string): Promise<string> {
  const { data, error } = await supabase.rpc("generate_retry_exam", {
    p_parent_exam_id: parentExamId,
  });

  if (error) {
    throw error;
  }
  return data as string;
}

/** 문항 응답 저장 */
export async function answerQuestion(
  questionId: string,
  selectedOption: number | null,
  isCorrect: boolean,
): Promise<void> {
  const { error } = await supabase
    .from("exam_questions")
    .update({
      selected_option: selectedOption,
      is_correct: isCorrect,
      answered_at: new Date().toISOString(),
    })
    .eq("id", questionId);

  if (error) {
    throw error;
  }
}

/** 시험 완료 처리 (PL/pgSQL 함수 호출) */
export async function completeExam(examId: string): Promise<number> {
  const { data, error } = await supabase.rpc("complete_exam", {
    p_exam_id: examId,
  });

  if (error) {
    throw error;
  }
  return data as number;
}
